import React, { useState } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { GlassButton } from 'dashboard/components/common';

/**
 * One ticket's conversation, and the box to answer it.
 *
 * The two sides are told apart by where they sit and what colour they are,
 * not by a label on each — a thread that writes "Brand:" and "Customer:" down
 * its margin reads like a transcript rather than a conversation.
 */
export default function TicketThread({ ticket, brandId, onChanged }) {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const send = () => {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    API.replyBrandTicket({ brand_id: brandId, _id: ticket._id, body })
      .then(() => { setReply(''); toast.success('Replied'); onChanged && onChanged(); })
      .catch((err) => toast.error(err?.message || 'Could not send that'))
      .finally(() => setSending(false));
  };

  const setStatus = (status) => {
    API.setBrandTicketStatus({ brand_id: brandId, _id: ticket._id, status })
      .then(() => {
        toast.success(status === 'closed' ? 'Closed' : 'Reopened');
        onChanged && onChanged();
      })
      .catch((err) => toast.error(err?.message || 'Could not change that'));
  };

  return (
    <Box mt='14px' pl={{ xs: 0, sm: '8px' }}>
      <Stack spacing='10px' mb='14px'>
        {(ticket.messages || []).map((m) => (
          <Box
            key={m._id}
            sx={{
              alignSelf: m.author === 'brand' ? 'flex-end' : 'flex-start',
              maxWidth: { xs: '100%', sm: '75%' },
              px: '14px',
              py: '10px',
              borderRadius: '14px',
              // The brand's own replies in the brand's colour, the customer's
              // on a neutral ground — the way every messaging app people
              // already use does it.
              bgcolor: m.author === 'brand' ? 'primary.main' : 'action.hover',
              color: m.author === 'brand' ? 'primary.contrastText' : 'text.primary',
            }}
          >
            <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>{m.body}</Typography>
            <Typography variant='caption' sx={{ opacity: 0.75, display: 'block', mt: '4px' }}>
              {new Date(m.created_at).toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Stack>

      <TextField
        fullWidth
        multiline
        minRows={2}
        size='small'
        placeholder='Write your reply…'
        value={reply}
        disabled={sending}
        onChange={(e) => setReply(e.target.value)}
      />
      <Stack direction='row' gap='8px' mt='8px' flexWrap='wrap'>
        <GlassButton
          variant='contained'
          onClick={send}
          disabled={sending || !reply.trim()}
          disabledReason={sending ? 'Sending…' : 'Write something first.'}
        >
          {sending ? 'Sending…' : 'Reply'}
        </GlassButton>
        {ticket.status === 'closed' ? (
          <GlassButton onClick={() => setStatus('open')}>Reopen</GlassButton>
        ) : (
          // Closing is offered, never automatic. A ticket that closes itself
          // on a reply is one the customer has to reopen to say "that did not
          // answer it", and most people just leave.
          <GlassButton onClick={() => setStatus('closed')}>Mark as done</GlassButton>
        )}
      </Stack>
    </Box>
  );
}
